import { InstantString } from "api/model/general/aliases";
import { ReportOptions } from "components/dashboard/product-template-table/ProductTemplateTable";
import {
  CellSubType,
  CellType,
  ColumnAggregationType,
  ProductTemplateSortingStrategy,
  ProductTemplateSortingStrategyOptionsOrder,
  SignStatus,
  TemplateLoadingType,
} from 'utils/enums';
import { ChronoUnitLegacy, TimeSpan } from 'utils/time-utils';
import { IForeignKeyValue } from "../components/templates/foreign-key/ForeignKey";
import { ITimespan, IUserSignature } from "./cellInterfaces";

export type Nullable<T> = T | null;

// Table config interface
export interface IProductTemplate {
    id?: number;
    displayName: string;
    safeName: string;
    keyColumn: string;
    metaKeyColumn: string;
    data?: IProductTemplateData;
    dateCreated?: InstantString;
    dateModified?: InstantString;
}

export interface IProductTemplateData {
    attributes: IProductTemplateAttribute[];
    config?: IProductTemplateConfig;
    parameterTemplates: IParameterSectionTemplate[];
}

export interface IProductTemplateAttribute { // TODO choose required
    referenceName: string;
    displayName: string;
    description?: string;
    type: CellType;
    subType?: CellSubType;
    unit?: string;
    shortenText?: boolean;
    decimalPlaces?: number;
    defaultBgColor?: string;
    defaultValue?: string | number;
    autoFillTime?: boolean;
    selectionValues?: Nullable<(TimeSpan | string)[]>;
    locked?: boolean;
    requiredPermission?: string;
    aggregationType?: ColumnAggregationType;
    evaluationExpression?: string;
    dateDisplayPattern?: string;
    foreignTemplate?: string;
    bgHighlightRanges?: Nullable<HighlightRange[]>;
    highlightColor?: Nullable<HighlightColor>;
    validationOptions?: ValidationOptions;
}

export interface IProductTemplateConfig {
    toStringFormatter?: string;
    startAttribute?: string;
    stopAttribute?: string;
    signatureAttribute?: string;
    rowsPerPage?: number;
    relations?: ITemplateRelation[];
    loadingType?: TemplateLoadingType;
    loadingConfig?: IPeriodLoadingConfig;
    sortingStrategy?: ProductTemplateSortingStrategy;
    sortingStrategyOptions?: ProductTemplateSortingStrategyOptions;
    sortingReversed?: boolean;
    displayTotalRow?: boolean;
    allowGenerateReport?: boolean;
    allowRowAddition?: boolean;
    allowRowDeletion?: boolean;
    allowRowEdit?: boolean;
    timezoneId?: string
}

export interface SignProductTemplateEntryResponse {
  display: string;
  id: number;
  signStatus: SignStatus;
  signed: boolean;
  signedAt?: string;
  validationErrors: ValidationError[];
}

export interface ValidationError {
  attribute: string;
  attributeDisplayName: string;
  message?: string;
  type: ValidationType;
}

interface IParameterSectionTemplate {
	sectionTitle: string;
	parameters: any[];
}

export interface LoadingConfig {
    loadingType?: TemplateLoadingType;
    loadingConfig?: IPeriodLoadingConfig;
}

interface ITemplateRelation {
    templateReference?: string;
    joinAttributes?: Map<string, string>;
}

export interface IPeriodLoadingConfig {
	defaultConfigUnit?: ChronoUnitLegacy;
	truncateStartUnit?: ChronoUnitLegacy;
	periodUnit?: ChronoUnitLegacy;
	period?: number;
	offsetUnit?: ChronoUnitLegacy;
	offset?: number;
}

export interface ProductTemplateSortingStrategyOptions {
    columns?: ProductTemplateColumnSortOptions[];
}

export interface ProductTemplateColumnSortOptions {
    column: string;
    order?: Nullable<ProductTemplateSortingStrategyOptionsOrder>;
}


// Table data interface
export interface ITableDataEntry {
    id?: string;
    template: string;
    entries: Record<string, ITableDataEntryValue>;
    dateCreated: string;
    dateModified: string;
    keyValue: any;
}

export interface ITableDataEntryId {
    timestamp?: number;
    counter?: number;
    randomValue1?: number;
    randomValue2?: number;
}

export interface ITableDataEntryValue {
    referenceName: string;
    type: string;
    value: number | string | IUserSignature | ITimespan | IForeignKeyValue;
}

export interface IDateRange {
    start?: Date,
    end?: Date
}

export interface HighlightRangeValue {
    value: number;
    inclusive: boolean;
}

export interface HighlightRange {
    min?: HighlightRangeValue
    max?: HighlightRangeValue
    color: string
    bgColor: string
}

export interface HighlightColor {
	color?: string;
	bgColor?: string;
}

export interface ValidationOptions {
  requiredAttributes?: string[];
  validators?: Validation[];
}

export interface Validation {
  message?: string;
  value?: string;
  type: ValidationType;
}

export type ValidationTypeInfo = {
  short: string;
  long: string;
};

export const commonValidationTypes = [
  { short: "EQ", long: "Equal to" },
  { short: "NE", long: "Not equal to" },
  { short: "REGEX", long: "Match regex" },
  { short: "MIN_LENGTH", long: "Min length" },
  { short: "MAX_LENGTH", long: "Max length" },
  { short: "CONTAINS", long: "Contains" },
  { short: "STARTS_WITH", long: "Starts with" },
  { short: "ENDS_WITH", long: "Ends with" },
  { short: "NOT_EMPTY", long: "Not empty" },
] as const;

export const validationTypes = [
  { short: "GT", long: "Greater than" },
  { short: "GTE", long: "Greater or equal to" },
  { short: "LT", long: "Less than" },
  { short: "LTE", long: "Less or equal to" },
  ...commonValidationTypes,
] as const;

export type ValidationType = (typeof validationTypes)[number]["short"];

export namespace ProductTemplate {
    export interface IHorizontalCommonConfig {
        editColumnLocation?: EditColumnLocation
    }

    export type EditColumnLocation = "FRONT" | "BACK"
}

export interface ProductTemplateNonTransposedTableProps extends ProductTemplate.IHorizontalCommonConfig {
    configData: IProductTemplate | undefined;
    templateData: Array<any>;

    selectedEntryIndex: number | undefined;
    selectedEntryId: string | undefined;
    selectedEntryBody: ITableDataEntry | undefined;
    page: number;
    setPage: (page: number) => void;
    allowRowEdit: boolean;
    foreignKeyWrap?: boolean;
    showTotals?: boolean;
    isTemplateDataFetching?: boolean;

    itemsPerPage?: number;

    reportOptions?: ReportOptions;

    EditRowDataCell(val: any, index: number): JSX.Element;

    editedDataHandler(indexEntry: number, refName: string, newValue: any, newCell?: ITableDataEntryValue): void;

    onClickSign(entryIndex: number, referenceName: string): void;

    handleTableKeyDown(key: string): void;
}

export interface IColumnsWithValuesProps {
    aggregationType?: ColumnAggregationType;
    referenceName: string;
    decimalPlaces?: number;
    values: Array<any>;
    total?: number;
    unit?: string;
}